// Gallery — /<slug>/gallery
// Groups media by stable guest_id (fallback: normalized uploader_name) into
// "From <Name>" folder cards. Each card lets you filter by subfolder.
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Download, MessageSquare, Play, ChevronDown, ChevronUp, User, Inbox, Folder, CheckSquare, Square, X as XIcon, Smartphone, FileArchive, Trash2, Sparkles, Plus, FolderInput, Share2, FolderPlus, Check, Crown, GripVertical, Palette, Image as ImgIcon, Pencil, ScanFace, Bookmark } from "lucide-react";
import PageShell from "../components/PageShell";
import MoveToFolderModal from "../components/MoveToFolderModal";
import Lightbox from "../components/Lightbox";
import GallerySettingsMenu from "../components/GallerySettingsMenu";
import SlideshowModal from "../components/SlideshowModal";
import FindMeModal from "../components/FindMeModal";
import PeopleModal from "../components/PeopleModal";
import PasscodeGate from "../components/PasscodeGate";
import GuestFacePills from "../components/GuestFacePills";
import { extractEmbeddings, loadImage as loadFaceImage } from "../lib/faceApi";
import { Albums, Media, Messages, Subfolders, Faces, VirtualAlbums } from "../lib/api";
import FaceRegisterModal from "../components/FaceRegisterModal";
import { getIconConfig } from "../lib/eventIcons";
import { useAuth } from "../lib/AuthContext";
import { getGuestId } from "../lib/guestId";
import { saveItemsToDevice, downloadZipSelection } from "../lib/download";
import { useLongPress } from "../hooks/useLongPress";
import useAutoJoin from "../hooks/useAutoJoin";

// Brand palette for folder coloring. `null` value = clear the color.
const FOLDER_COLORS = [
  { name: "Default", value: null },
  { name: "Purple",  value: "#7F4BFF" },
  { name: "Cyan",    value: "#22E2FF" },
  { name: "Magenta", value: "#FF4D8F" },
  { name: "Orange",  value: "#FF8A3D" },
  { name: "Amber",   value: "#FFD24D" },
  { name: "Green",   value: "#3DD9A8" },
  { name: "Blue",    value: "#4D8FFF" },
];

export default function GalleryPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [album, setAlbum] = useState(null);
  useAutoJoin(album); // signed-in non-owners get this album on their dashboard
  const [media, setMedia] = useState([]);
  const [subfolders, setSubfolders] = useState([]);
  const [activeUploader, setActiveUploader] = useState(null); // group key or null
  const [activeSubfolder, setActiveSubfolder] = useState(null); // subfolder id or null
  const [loading, setLoading] = useState(true);
  const [unread, setUnread] = useState(0);
  // Multi-select state
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [busyAction, setBusyAction] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [freshFolderId, setFreshFolderId] = useState(null); // glows for 5s
  const [slideshowOpen, setSlideshowOpen] = useState(false);
  const [findMeOpen, setFindMeOpen] = useState(false);
  // When set, gallery is filtered to only show these media ids ("Find me" self-search).
  const [faceMatchIds, setFaceMatchIds] = useState(null);
  // Registered guests (with saved face descriptors) + multi-person virtual album selection.
  const [registrations, setRegistrations] = useState([]);
  const [activeFaceGuests, setActiveFaceGuests] = useState([]); // [{guest_id, name}, ...]
  const [faceMatchMap, setFaceMatchMap] = useState({}); // guest_id -> [media ids]
  const [faceMode, setFaceMode] = useState("and"); // "and" = together, "or" = any
  const [peopleOpen, setPeopleOpen] = useState(false);
  const [virtualAlbums, setVirtualAlbums] = useState([]); // this guest's saved combos
  const [vaSaving, setVaSaving] = useState(false);   // banner "save" inline input open
  const [vaName, setVaName] = useState("");
  const [faceRegOpen, setFaceRegOpen] = useState(false); // "Add your face" from gallery
  const [indexProgress, setIndexProgress] = useState({ done: 0, total: 0 });
  const [showFreshTip, setShowFreshTip] = useState(false);  // only true on the first create per browser
  const [lightboxId, setLightboxId] = useState(null);       // id of media currently in the lightbox

  const toggleSelected = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const clearSelection = () => setSelected(new Set());
  const exitSelectMode = () => { setSelectMode(false); clearSelection(); };
  const enterSelectMode = () => { setSelectMode(true); };

  // Triggered by a long-press on a tile — flip into select mode AND mark this tile.
  const enterSelectWith = (id) => {
    setSelectMode(true);
    setSelected((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };

  const selectAllInItems = (items) => {
    // Adding items to the selection implicitly enters select mode — otherwise
    // the bottom action bar wouldn't appear and the ticked tiles wouldn't render.
    setSelectMode(true);
    setSelected((prev) => {
      const next = new Set(prev);
      for (const m of items) next.add(m.id);
      return next;
    });
  };

  const handleSaveToDevice = async () => {
    const items = media.filter((m) => selected.has(m.id));
    if (items.length === 0) return;
    setBusyAction(true);
    const tid = toast.loading(`Preparing ${items.length} file${items.length === 1 ? "" : "s"}…`);
    try {
      const res = await saveItemsToDevice(items, (done, total) => {
        toast.loading(`Downloading ${done}/${total}…`, { id: tid });
      });
      toast.dismiss(tid);
      if (res.ok && res.mode === "share") {
        toast.success("Saved to your device");
      } else if (res.ok) {
        toast.success(`${items.length} download${items.length === 1 ? "" : "s"} started`);
      } else if (res.reason === "cancelled") {
        toast.message("Cancelled");
      } else {
        toast.error("Nothing to download");
      }
    } catch (e) {
      toast.dismiss(tid);
      toast.error("Download failed");
    } finally {
      setBusyAction(false);
    }
  };

  const handleZipSelection = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    setBusyAction(true);
    const tid = toast.loading(`Zipping ${ids.length} files…`);
    try {
      await downloadZipSelection(slug, ids);
      toast.dismiss(tid);
      toast.success("ZIP downloaded");
    } catch (e) {
      toast.dismiss(tid);
      toast.error(e?.response?.data?.detail || "ZIP failed");
    } finally {
      setBusyAction(false);
    }
  };

  const handleDeleteSelection = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    const msg = ids.length === 1
      ? "Delete this item?"
      : `Delete ${ids.length} items? Only photos/videos you uploaded will be removed (others are skipped).`;
    if (!window.confirm(msg)) return;
    setBusyAction(true);
    const tid = toast.loading(`Deleting ${ids.length}…`);
    try {
      const res = await Media.deleteSelection(slug, ids);
      toast.dismiss(tid);
      const { deleted = 0, skipped = 0 } = res || {};
      if (deleted === 0 && skipped > 0) {
        toast.error("Nothing deleted — those weren't yours to delete");
      } else if (skipped > 0) {
        toast.success(`${deleted} deleted · ${skipped} weren't yours`);
      } else {
        toast.success(`${deleted} deleted`);
      }
      clearSelection();
      await load();
    } catch (e) {
      toast.dismiss(tid);
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setBusyAction(false);
    }
  };

  const handleMoveSelection = async (subfolderId, newName) => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    setBusyAction(true);
    const tid = toast.loading("Moving…");
    try {
      const res = await Media.moveSelection(slug, ids, {
        subfolder_id: subfolderId,
        new_subfolder_name: newName,
      });
      toast.dismiss(tid);
      const { moved = 0, skipped = 0 } = res || {};
      if (moved === 0 && skipped > 0) {
        toast.error("Nothing moved — those weren't yours to move");
      } else if (skipped > 0) {
        toast.success(`${moved} moved · ${skipped} weren't yours`);
      } else {
        toast.success(`${moved} moved`);
      }
      setMoveOpen(false);
      clearSelection();
      exitSelectMode();
      await load();
    } catch (e) {
      toast.dismiss(tid);
      toast.error(e?.response?.data?.detail || "Move failed");
    } finally {
      setBusyAction(false);
    }
  };

  // Share/forward — uses the Web Share API where available (iOS/Android native sheet),
  // falls back to copying the album URL on desktop. We share the album URL plus a
  // short note about the selected items.
  const handleShareSelection = async () => {
    const count = selected.size;
    if (count === 0) return;
    const url = `${window.location.origin}/${slug}`;
    const title = album ? `${album.name} — mEYEcam` : "mEYEcam";
    const text = count === 1
      ? `Check out this moment from "${album?.name || "the album"}" on mEYEcam: ${url}`
      : `Check out ${count} moments from "${album?.name || "the album"}" on mEYEcam: ${url}`;
    if (navigator.share) {
      try {
        await navigator.share({ title, text, url });
        return;
      } catch (err) {
        if (err?.name === "AbortError") return;
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Album link copied");
    } catch (_) {
      toast.error("Couldn't share — long-press the link to copy");
    }
  };

  // Standalone "create folder" — used by the gallery's "+ New folder" button so
  // users can pre-create empty folders before selecting items. (The Move modal
  // also lets you create-and-move in one step.)
  const handleCreateFolder = async () => {
    const name = newFolderName.trim();
    if (!name || creatingFolder) return;
    setCreatingFolder(true);
    try {
      // Host-created folders are shared (creator_guest_id=null) so every guest sees them.
      // Guest-created folders stay private to that guest (+ the host).
      const created = await Subfolders.create(slug, name, isHost ? null : getGuestId());
      toast.success(`Folder "${name}" created`);
      setNewFolderName("");
      setNewFolderOpen(false);
      // Refresh the subfolders list so the Move modal sees it immediately
      const sf = await Subfolders.list(slug);
      setSubfolders(sf);
      // Highlight the fresh pill for 5s with a glow + "NEW" sparkle badge.
      // The instructional tip bubble only appears on the user's FIRST folder
      // ever (per-browser) so repeat creators don't get nagged.
      if (created?.id) {
        setFreshFolderId(created.id);
        let firstEver = false;
        try {
          firstEver = !localStorage.getItem("meyecam.tip_seen_select");
          if (firstEver) localStorage.setItem("meyecam.tip_seen_select", "1");
        } catch (_) {
          firstEver = true;
        }
        setShowFreshTip(firstEver);
        setTimeout(() => {
          setFreshFolderId((id) => (id === created.id ? null : id));
          setShowFreshTip(false);
        }, 5500);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create folder");
    } finally {
      setCreatingFolder(false);
    }
  };

  const load = async () => {
    try {
      const a = await Albums.get(slug);
      setAlbum(a);
      // If locked and no host bypass, don't fetch content — the gate will render.
      if (a.requires_passcode && !a.owner_id) {
        setLoading(false);
        return;
      }
      const [m, sf] = await Promise.all([
        Media.list(slug),
        Subfolders.list(slug),
      ]);
      setMedia(m);
      setSubfolders(sf);
    } catch (e) {
      toast.error("Could not load gallery");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug]);

  // Fetch face registrations once the album is loaded + unlocked
  const refreshRegistrations = React.useCallback(() => {
    if (!album || (album.requires_passcode && !album.owner_id)) return;
    Faces.registrations(slug).then(setRegistrations).catch(() => {});
  }, [album, slug]);

  useEffect(() => { refreshRegistrations(); }, [refreshRegistrations]);

  // Background face-indexing: for every photo not yet indexed on the server,
  // compute its face descriptors client-side and POST them back so that
  // per-guest virtual albums can be built. Runs quietly in the background.
  useEffect(() => {
    if (!album || (album.requires_passcode && !album.owner_id)) return;
    if (!Array.isArray(media) || media.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        const photos = media.filter((m) => !m.is_video && !m.is_deleted);
        const { ids: alreadyIndexed } = await Faces.indexedIds(slug);
        const indexedSet = new Set(alreadyIndexed || []);
        const todo = photos.filter((p) => !indexedSet.has(p.id));
        setIndexProgress({ done: photos.length - todo.length, total: photos.length });
        for (const p of todo) {
          if (cancelled) return;
          try {
            const img = await loadFaceImage(Media.thumbUrl(p.id));
            const descs = await extractEmbeddings(img);
            await Faces.savePhoto(slug, p.id, descs);
          } catch (_) { /* skip unreadable photos */ }
          setIndexProgress((prev) => ({ done: prev.done + 1, total: photos.length }));
        }
        // After the album is fully indexed, refresh registrations so newly
        // registered folks show up + refresh any active face-match filter.
        if (!cancelled) refreshRegistrations();
      } catch (_) { /* non-blocking */ }
    })();
    return () => { cancelled = true; };
  }, [album, media, slug, refreshRegistrations]);

  // Toggle a person in/out of the multi-select. With several people selected,
  // the gallery shows the INTERSECTION — photos where they appear together.
  const toggleGuestFace = async (reg) => {
    setFaceMatchIds(null); // person filters replace the "Find me" self-search
    const isActive = activeFaceGuests.some((g) => g.guest_id === reg.guest_id);
    if (isActive) {
      setActiveFaceGuests((prev) => prev.filter((g) => g.guest_id !== reg.guest_id));
      return;
    }
    try {
      const res = await Faces.matches(slug, reg.guest_id);
      setFaceMatchMap((prev) => ({ ...prev, [reg.guest_id]: res.ids || [] }));
      setActiveFaceGuests((prev) => [...prev, reg]);
      if ((res.ids || []).length === 0) {
        toast.info(`No photos of ${reg.name} yet — try again after more indexing.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't fetch matches");
    }
  };

  const clearFaceFilter = () => {
    setActiveFaceGuests([]);
    setFaceMatchIds(null);
    setVaSaving(false);
  };

  // The combined face filter: intersection ("and") or union ("or") of every
  // selected person's matches, or the "Find me" self-search result.
  const combinedFaceIds = useMemo(() => {
    if (activeFaceGuests.length > 0) {
      if (faceMode === "or") {
        const union = new Set();
        for (const g of activeFaceGuests) {
          for (const id of faceMatchMap[g.guest_id] || []) union.add(id);
        }
        return [...union];
      }
      let ids = null;
      for (const g of activeFaceGuests) {
        const set = new Set(faceMatchMap[g.guest_id] || []);
        ids = ids === null ? set : new Set([...ids].filter((id) => set.has(id)));
      }
      return [...(ids || [])];
    }
    return Array.isArray(faceMatchIds) ? faceMatchIds : null;
  }, [activeFaceGuests, faceMatchMap, faceMatchIds, faceMode]);

  // ---- Saved virtual albums (private per guest) ----
  const refreshVirtualAlbums = React.useCallback(() => {
    if (!album || (album.requires_passcode && !album.owner_id)) return;
    VirtualAlbums.list(slug, getGuestId()).then(setVirtualAlbums).catch(() => {});
  }, [album, slug]);

  useEffect(() => { refreshVirtualAlbums(); }, [refreshVirtualAlbums]);

  const saveVirtualAlbum = async () => {
    const nm = vaName.trim();
    if (!nm || activeFaceGuests.length === 0) return;
    try {
      await VirtualAlbums.create(slug, {
        name: nm,
        guest_ids: activeFaceGuests.map((g) => g.guest_id),
        mode: faceMode,
        creator_guest_id: getGuestId(),
      });
      setVaSaving(false);
      setVaName("");
      toast.success(`"${nm}" saved — find it above the people pills`);
      refreshVirtualAlbums();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save virtual album");
    }
  };

  const applyVirtualAlbum = async (va) => {
    setFaceMatchIds(null);
    setFaceMode(va.mode === "or" ? "or" : "and");
    const regsById = new Map(registrations.map((r) => [r.guest_id, r]));
    const members = [];
    const map = {};
    for (const gid of va.guest_ids) {
      try {
        const res = await Faces.matches(slug, gid);
        map[gid] = res.ids || [];
        members.push(regsById.get(gid) || { guest_id: gid, name: res.name || "Guest" });
      } catch (_) { /* person may have unregistered — skip */ }
    }
    if (members.length === 0) {
      toast.error("The people in this virtual album are no longer registered");
      return;
    }
    setFaceMatchMap((prev) => ({ ...prev, ...map }));
    setActiveFaceGuests(members);
  };

  const deleteVirtualAlbum = async (va) => {
    try {
      await VirtualAlbums.remove(slug, va.id, getGuestId());
      toast.success(`Deleted "${va.name}"`);
      refreshVirtualAlbums();
    } catch (_) {
      toast.error("Couldn't delete");
    }
  };

  // Select every currently visible face match (for bulk download/share/organize)
  const selectAllMatches = () => {
    if (!Array.isArray(combinedFaceIds)) return;
    const visible = new Set(filteredMedia.map((m) => m.id));
    setSelected(new Set(combinedFaceIds.filter((id) => visible.has(id))));
    setSelectMode(true);
  };

  // Fetch unread message count when user is the host
  useEffect(() => {
    if (!user || !album || !album.owner_id || album.owner_id !== user.user_id) {
      setUnread(0);
      return;
    }
    Messages.unread(slug).then((r) => setUnread(r.unread)).catch(() => {});
  }, [user, album, slug]);

  const isHost = !!(user && album && album.owner_id && album.owner_id === user.user_id);

  // Subfolder counts across ALL uploaders — used by the top-level Folders row.
  // We compute on the full media list (not the per-uploader filtered list) so the
  // counts stay stable as the user toggles the uploader chip filter.
  const subfolderCounts = useMemo(() => {
    const m = new Map();
    for (const it of media) {
      if (it.subfolder_id) m.set(it.subfolder_id, (m.get(it.subfolder_id) || 0) + 1);
    }
    return m;
  }, [media]);

  // What subfolders to render in the top row.
  // - Host sees every folder in the album.
  // - Guests see folders they created OR legacy folders without a creator.
  const visibleSubfolders = useMemo(() => {
    if (isHost) return subfolders;
    const myGid = getGuestId();
    return subfolders.filter(
      (s) => !s.creator_guest_id || s.creator_guest_id === myGid
    );
  }, [subfolders, isHost]);

  // Apply the top-level subfolder filter to media before grouping by uploader.
  // Special id "__inbox__" filters to items WITHOUT any subfolder_id.
  const filteredMedia = useMemo(() => {
    let out = media;
    if (activeSubfolder === "__inbox__") {
      out = out.filter((m) => !m.subfolder_id);
    } else if (activeSubfolder) {
      out = out.filter((m) => m.subfolder_id === activeSubfolder);
    }
    // Face-match filter overlays on top of the subfolder filter.
    if (Array.isArray(combinedFaceIds)) {
      const set = new Set(combinedFaceIds);
      out = out.filter((m) => set.has(m.id));
    }
    return out;
  }, [media, activeSubfolder, combinedFaceIds]);

  // Count of items that aren't filed into any subfolder. Used to decide whether
  // to show the "Inbox" pseudo-folder pill.
  const unfiledCount = useMemo(
    () => media.reduce((n, m) => n + (m.subfolder_id ? 0 : 1), 0),
    [media]
  );

  // Currently-active subfolder doc (if any) — for the breadcrumb.
  const activeFolder = useMemo(() => {
    if (!activeSubfolder || activeSubfolder === "__inbox__") return null;
    return visibleSubfolders.find((s) => s.id === activeSubfolder) || null;
  }, [activeSubfolder, visibleSubfolders]);

  // Group media by guest_id (preferred) or normalized uploader_name (legacy)
  const groups = useMemo(() => {
    const order = [];
    const byKey = new Map();
    // newest items last so latest name wins
    const sorted = [...filteredMedia].sort((a, b) => (a.created_at < b.created_at ? -1 : 1));
    for (const m of sorted) {
      const cleanName = (m.uploader_name || "Guest").trim() || "Guest";
      const key = m.guest_id ? `g:${m.guest_id}` : `n:${cleanName.toLowerCase()}`;
      if (!byKey.has(key)) {
        byKey.set(key, { key, name: cleanName, items: [] });
        order.push(key);
      }
      const g = byKey.get(key);
      g.name = cleanName; // most recent name wins
      g.items.push(m);
    }
    // newest-uploader-first order for display
    return order.map((k) => byKey.get(k)).reverse();
  }, [filteredMedia]);

  // Leaderboard: rank groups by number of contributions (desc). Stable across
  // renders for the same data. Returns a Map<groupKey, { rank, total }>.
  const groupRanks = useMemo(() => {
    const sorted = [...groups].sort((a, b) => (b.items.length - a.items.length));
    const ranks = new Map();
    sorted.forEach((g, i) => ranks.set(g.key, { rank: i + 1, total: g.items.length }));
    return ranks;
  }, [groups]);

  // Flat ordered list of currently-visible media — used by the lightbox to
  // compute prev/next. We follow the on-screen visual order (group by uploader,
  // then by created_at within each group, newest-uploader-first).
  const flatOrdered = useMemo(() => {
    return groups.flatMap((g) => g.items);
  }, [groups]);

  const openLightbox = (id) => setLightboxId(id);
  const closeLightbox = () => setLightboxId(null);

  // Stable array of selected ids — used to feed drag payloads when batching.
  const selectedIds = useMemo(() => Array.from(selected), [selected]);

  const downloadFolderZip = async (sub) => {
    const ids = media.filter((m) => m.subfolder_id === sub.id).map((m) => m.id);
    if (ids.length === 0) { toast.error("This folder is empty"); return; }
    const tid = toast.loading(`Zipping "${sub.name}"…`);
    try {
      const API = process.env.REACT_APP_BACKEND_URL || "";
      const res = await fetch(`${API}/api/album/${slug}/zip-selection`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids }),
      });
      if (!res.ok) throw new Error("zip failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${slug}-${sub.name.replace(/\W+/g, "-").toLowerCase()}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      toast.dismiss(tid);
      toast.success(`Downloaded ${ids.length} items from ${sub.name}`);
    } catch (_) {
      toast.dismiss(tid);
      toast.error("ZIP failed — try again");
    }
  };

  // Host-only: drag a folder pill onto another to reorder. We track which pill
  // is being dragged in component state (NOT in dataTransfer) because media
  // tile drags use a separate MIME type. The drag image is the pill itself.
  const [draggingFolderId, setDraggingFolderId] = useState(null);
  const [dragOverFolderId, setDragOverFolderId] = useState(null);
  const [colorPopoverFor, setColorPopoverFor] = useState(null);
  // One-time "How to organize" banner. Visible only when the album has at
  // least one folder AND the user hasn't dismissed it before.
  const [showOrganizeTip, setShowOrganizeTip] = useState(() => {
    try { return !localStorage.getItem("meyecam.tip_organize"); }
    catch (_) { return true; }
  });
  const dismissOrganizeTip = () => {
    try { localStorage.setItem("meyecam.tip_organize", "1"); } catch (_) { /* noop */ }
    setShowOrganizeTip(false);
  };

  // Inline-rename state for the breadcrumb pencil
  const [renamingFolderId, setRenamingFolderId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const startRenameFolder = (sub) => {
    setRenameValue(sub.name);
    setRenamingFolderId(sub.id);
  };
  const cancelRenameFolder = () => {
    setRenamingFolderId(null);
    setRenameValue("");
  };
  const commitRenameFolder = async () => {
    const next = renameValue.trim();
    const sub = visibleSubfolders.find((s) => s.id === renamingFolderId);
    if (!sub) { cancelRenameFolder(); return; }
    if (!next || next === sub.name) { cancelRenameFolder(); return; }
    try {
      const updated = await Subfolders.patch(sub.id, { name: next });
      setSubfolders((prev) => prev.map((s) => (s.id === sub.id ? { ...s, name: updated.name } : s)));
      toast.success("Folder renamed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't rename");
    } finally {
      cancelRenameFolder();
    }
  };

  // Can the current viewer edit this folder? (host OR original creator)
  const canEditFolder = (sub) => {
    if (!sub) return false;
    if (isHost) return true;
    return !!(sub.creator_guest_id && sub.creator_guest_id === getGuestId());
  };

  const handleReorderFolders = async (sourceId, targetId) => {
    if (sourceId === targetId) return;
    const currentOrder = visibleSubfolders.map((s) => s.id);
    const fromIdx = currentOrder.indexOf(sourceId);
    const toIdx = currentOrder.indexOf(targetId);
    if (fromIdx < 0 || toIdx < 0) return;
    const next = [...currentOrder];
    next.splice(fromIdx, 1);
    next.splice(toIdx, 0, sourceId);
    // Optimistic local sort so the UI updates instantly
    const order = new Map(next.map((id, i) => [id, i]));
    setSubfolders((prev) =>
      [...prev].sort((a, b) => (order.get(a.id) ?? 999) - (order.get(b.id) ?? 999))
    );
    try {
      await Subfolders.reorder(slug, next);
      const sf = await Subfolders.list(slug);
      setSubfolders(sf);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't reorder");
      // Reload original order on failure
      const sf = await Subfolders.list(slug);
      setSubfolders(sf);
    }
  };

  const handleRecolorFolder = async (sid, color) => {
    try {
      const updated = await Subfolders.patch(sid, { color });
      setSubfolders((prev) => prev.map((s) => (s.id === sid ? { ...s, color: updated.color } : s)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't change color");
    }
  };

  // Drop a payload from a tile onto a folder pill — calls the bulk move endpoint.
  const handleDropOnFolder = async (subfolderId, payloadIds) => {
    if (!payloadIds || payloadIds.length === 0) return;
    setBusyAction(true);
    const tid = toast.loading(`Moving ${payloadIds.length}…`);
    try {
      const res = await Media.moveSelection(slug, payloadIds, { subfolder_id: subfolderId });
      toast.dismiss(tid);
      const { moved = 0, skipped = 0 } = res || {};
      if (moved === 0 && skipped > 0) {
        toast.error("Nothing moved — those weren't yours to move");
      } else if (skipped > 0) {
        toast.success(`${moved} moved · ${skipped} weren't yours`);
      } else {
        toast.success(`${moved} moved`);
      }
      clearSelection();
      exitSelectMode();
      await load();
    } catch (e) {
      toast.dismiss(tid);
      toast.error(e?.response?.data?.detail || "Move failed");
    } finally {
      setBusyAction(false);
    }
  };

  const visibleGroups = activeUploader
    ? groups.filter((g) => g.key === activeUploader)
    : groups;

  const myKey = `g:${getGuestId()}`;
  const myGroup = groups.find((g) => g.key === myKey);

  if (loading || !album) {
    return <PageShell wide><div className="flex justify-center mt-24"><div className="meye-spinner" /></div></PageShell>;
  }

  // Passcode gate — album exists but requires unlocking. Locked responses
  // strip owner_id and cover, so requires_passcode && !owner_id => locked.
  if (album.requires_passcode && !album.owner_id) {
    return <PasscodeGate album={album} onUnlocked={() => load()} />;
  }

  const cfg = getIconConfig(album.icon);
  const Icon = cfg.Icon;

  return (
    <PageShell
      wide
      headerLeft={
        <button
          onClick={() => {
            // Hosts of this album go to their host home;
            // guests go to the album landing (or the previous page if there's history).
            if (isHost) {
              navigate("/");
            } else if (window.history.length > 1) {
              navigate(-1);
            } else {
              navigate(`/${slug}`);
            }
          }}
          className="meye-chip"
          style={{ padding: "8px 12px" }}
          data-testid="back-to-landing-gallery"
        >
          <ArrowLeft size={14} /> Back
        </button>
      }
    >
      <div className="flex items-center gap-3 mt-1 mb-5">
        <div
          className="flex items-center justify-center"
          style={{ width: 48, height: 48, borderRadius: 12, background: `${cfg.color}22`, border: `1px solid ${cfg.color}66` }}
        >
          <Icon size={22} color={cfg.color} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xl font-semibold tracking-tight truncate" data-testid="gallery-title">{album.name}</div>
          <div className="text-xs" style={{ color: "rgba(255,255,255,0.5)" }}>
            {media.length} {media.length === 1 ? "item" : "items"} · {groups.length} {groups.length === 1 ? "contributor" : "contributors"}
          </div>
        </div>
        {isHost && (
          <GallerySettingsMenu
            album={album}
            onUpdated={(updated) => setAlbum(updated)}
            onDeleted={() => navigate("/")}
          />
        )}
      </div>

      <div className="flex flex-col gap-2 mb-3">
        <Link
          to={`/${slug}/upload`}
          className="inline-flex items-center justify-center gap-2 rounded-full font-semibold text-sm px-4 py-3"
          style={{
            background: "linear-gradient(90deg, #7F4BFF 0%, #4D1EFF 50%, #22E2FF 100%)",
            color: "#fff",
            boxShadow: "0 14px 36px -12px rgba(127,75,255,0.55)",
          }}
          data-testid="add-to-album-btn"
        >
          <Plus size={16} />
          <span>Add photos & videos</span>
        </Link>
      </div>

      <div className="flex gap-2 mb-5 flex-wrap">
        <button
          type="button"
          onClick={() => {
            if (media.length === 0) { toast.error("Upload some photos first"); return; }
            setSlideshowOpen(true);
          }}
          className="meye-chip flex-1 justify-center"
          style={{ background: "rgba(127,75,255,0.10)", borderColor: "rgba(127,75,255,0.45)", color: "#C9B6FF", minWidth: 140 }}
          data-testid="play-slideshow-btn"
          title="Full-screen slideshow with music"
        >
          <Play size={14} fill="#C9B6FF" /> Play slideshow
        </button>
        <button
          type="button"
          onClick={() => {
            if (media.length === 0) { toast.error("Upload some photos first"); return; }
            setFindMeOpen(true);
          }}
          className="meye-chip flex-1 justify-center"
          style={{ background: "rgba(255,31,107,0.10)", borderColor: "rgba(255,31,107,0.45)", color: "#FF9DBE", minWidth: 140 }}
          data-testid="find-me-btn"
          title="Snap a selfie to find photos of you in this album"
        >
          <ScanFace size={14} /> Find me
        </button>
        <a
          href={Media.zipUrl(slug)}
          target="_blank"
          rel="noreferrer"
          className="meye-chip flex-1 justify-center"
          style={{ background: "rgba(34,226,255,0.08)", borderColor: "rgba(34,226,255,0.35)", minWidth: 140 }}
          data-testid="download-zip-btn"
        >
          <Download size={14} /> Download ZIP
        </a>
        <Link
          to={`/${slug}/message`}
          className="meye-chip flex-1 justify-center"
          style={{ background: "rgba(127,75,255,0.08)", borderColor: "rgba(127,75,255,0.35)", minWidth: 140 }}
          data-testid="message-host-btn"
        >
          <MessageSquare size={14} color="#7F4BFF" /> Message the Host
        </Link>
        <button
          type="button"
          onClick={() => setNewFolderOpen(true)}
          className="meye-chip flex-1 justify-center"
          style={{
            background: "rgba(127,75,255,0.10)",
            borderColor: "rgba(127,75,255,0.45)",
            color: "#C9B6FF",
            minWidth: 140,
          }}
          data-testid="new-folder-btn"
          title="Create a folder — then long-press tiles or hit Select to move items into it"
        >
          <FolderPlus size={14} /> New folder
        </button>
        {!selectMode ? null : (
          <button
            onClick={exitSelectMode}
            className="meye-chip flex-1 justify-center"
            style={{ background: "rgba(255,107,107,0.08)", borderColor: "rgba(255,107,107,0.35)", color: "#FF6B6B", minWidth: 140 }}
            data-testid="exit-select-mode-btn"
          >
            <XIcon size={14} /> Cancel
          </button>
        )}
      </div>

      {Array.isArray(combinedFaceIds) && (
        <div
          className="flex items-center gap-3 rounded-2xl px-4 py-3 mb-4"
          style={{
            background: "linear-gradient(90deg, rgba(255,122,0,0.10), rgba(255,31,107,0.12))",
            border: "1px solid rgba(255,31,107,0.35)",
          }}
          data-testid="face-match-banner"
        >
          <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
               style={{ background: "rgba(255,31,107,0.16)", border: "1px solid rgba(255,31,107,0.45)" }}>
            <ScanFace size={16} color="#FF9DBE" />
          </div>
          <div className="flex-1 min-w-0 text-sm">
            <div className="font-semibold">
              {activeFaceGuests.length > 0
                ? `Photos with ${activeFaceGuests.map((g) => g.name).join(faceMode === "or" ? " / " : " + ")}`
                : "Filtered to face matches"}
            </div>
            <div className="text-xs" style={{ color: "rgba(255,255,255,0.6)" }}>
              Showing {combinedFaceIds.length} photo{combinedFaceIds.length === 1 ? "" : "s"}
              {activeFaceGuests.length > 1
                ? (faceMode === "or" ? " with any of them" : " where they appear together")
                : activeFaceGuests.length === 1
                  ? " across all contributors"
                  : " we think you\u2019re in"}
            </div>
          </div>
          {combinedFaceIds.length > 0 && (
            <button
              type="button"
              onClick={selectAllMatches}
              className="meye-chip"
              style={{ padding: "6px 12px" }}
              data-testid="select-all-matches"
              title="Select every match for download or sharing"
            >
              Select all {combinedFaceIds.length}
            </button>
          )}
          {activeFaceGuests.length > 0 && !vaSaving && (
            <button
              type="button"
              onClick={() => {
                setVaName(activeFaceGuests.map((g) => g.name).join(faceMode === "or" ? " / " : " + "));
                setVaSaving(true);
              }}
              className="meye-chip"
              style={{ padding: "6px 12px", borderColor: "rgba(255,122,0,0.5)", color: "#FFC299" }}
              data-testid="save-virtual-album-btn"
            >
              <Bookmark size={12} /> Save
            </button>
          )}
          <button
            type="button"
            onClick={clearFaceFilter}
            className="meye-chip"
            style={{ padding: "6px 12px" }}
            data-testid="clear-face-match"
          >
            <XIcon size={12} /> Clear
          </button>
        </div>
      )}

      {/* Inline "save virtual album" name input */}
      {Array.isArray(combinedFaceIds) && vaSaving && (
        <div className="flex items-center gap-2 mb-4" data-testid="virtual-album-save-row">
          <input
            autoFocus
            type="text"
            className="meye-input flex-1"
            style={{ padding: "8px 12px", fontSize: 13, maxWidth: 340 }}
            placeholder="Virtual album name"
            value={vaName}
            onChange={(e) => setVaName(e.target.value)}
            maxLength={60}
            onKeyDown={(e) => {
              if (e.key === "Enter") saveVirtualAlbum();
              if (e.key === "Escape") setVaSaving(false);
            }}
            data-testid="virtual-album-name-input"
          />
          <button
            type="button"
            onClick={saveVirtualAlbum}
            disabled={!vaName.trim()}
            className="meye-chip"
            style={{ padding: "8px 14px", borderColor: "rgba(34,255,183,0.5)", color: "#7CFFCB", opacity: vaName.trim() ? 1 : 0.5 }}
            data-testid="virtual-album-save-confirm"
          >
            Save album
          </button>
          <button
            type="button"
            onClick={() => setVaSaving(false)}
            className="meye-chip"
            style={{ padding: "8px 12px" }}
            data-testid="virtual-album-save-cancel"
          >
            Cancel
          </button>
        </div>
      )}

      <GuestFacePills
        registrations={registrations}
        activeGuestIds={activeFaceGuests.map((g) => g.guest_id)}
        indexingProgress={indexProgress}
        faceMode={faceMode}
        onModeChange={setFaceMode}
        virtualAlbums={virtualAlbums}
        onApplyVirtualAlbum={applyVirtualAlbum}
        onDeleteVirtualAlbum={deleteVirtualAlbum}
        onToggleGuest={toggleGuestFace}
        onClear={clearFaceFilter}
        onOpenPeople={() => setPeopleOpen(true)}
        onAddFace={() => setFaceRegOpen(true)}
      />

      <PeopleModal
        open={peopleOpen}
        onClose={() => setPeopleOpen(false)}
        registrations={registrations}
        activeGuestIds={activeFaceGuests.map((g) => g.guest_id)}
        faceMode={faceMode}
        onModeChange={setFaceMode}
        onToggleGuest={toggleGuestFace}
        onClear={clearFaceFilter}
      />

      <FaceRegisterModal
        open={faceRegOpen}
        onClose={() => setFaceRegOpen(false)}
        slug={slug}
        guestId={getGuestId()}
        name={localStorage.getItem("meyecam.display_name") || ""}
        onRegistered={() => refreshRegistrations()}
      />

      {selectMode && (
        <div
          className="glass-card p-3 mb-4 flex flex-wrap gap-2 items-center"
          data-testid="select-toolbar"
          style={{ borderColor: "rgba(127,75,255,0.45)", background: "rgba(127,75,255,0.06)" }}
        >
          <button
            onClick={() => selectAllInItems(media)}
            className="meye-chip"
            style={{ padding: "6px 10px", fontSize: 12 }}
            data-testid="select-all-btn"
          >
            Select all ({media.length})
          </button>
          <button
            onClick={clearSelection}
            className="meye-chip"
            style={{ padding: "6px 10px", fontSize: 12 }}
            data-testid="clear-selection-btn"
            disabled={selected.size === 0}
          >
            Clear
          </button>
          <div className="text-xs ml-auto" style={{ color: "rgba(255,255,255,0.7)" }}>
            <span data-testid="selected-count">{selected.size}</span> selected
          </div>
        </div>
      )}

      {/* Top-level Folders row — visible the moment a folder is created, even when empty.
          Lets the user click a folder to filter the gallery to JUST that folder's items
          (across every uploader). The Move modal and per-uploader chips still work as before. */}
      {visibleSubfolders.length > 0 && (
        <div className="mb-5" data-testid="folders-row">
          <div className="section-label mb-2 flex items-center gap-2">
            <FolderPlus size={11} color="#C9B6FF" /> FOLDERS
            <span className="hidden sm:inline text-[10px] font-normal" style={{ color: "rgba(255,255,255,0.40)" }}>
              · drag tiles onto a folder · double-click to ZIP
            </span>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: "thin" }}>
            {/* Inbox pseudo-folder — only shown when there ARE folders AND
                some items aren't filed yet. Disappears once everything's in a folder. */}
            {unfiledCount > 0 && (
              <button
                onClick={() => setActiveSubfolder(activeSubfolder === "__inbox__" ? null : "__inbox__")}
                className="flex items-center gap-2 rounded-xl px-3 py-2 shrink-0 transition"
                style={{
                  background: activeSubfolder === "__inbox__"
                    ? "linear-gradient(135deg, rgba(34,226,255,0.30), rgba(127,75,255,0.20))"
                    : "rgba(34,226,255,0.06)",
                  border: `1px solid ${activeSubfolder === "__inbox__" ? "#22E2FF" : "rgba(34,226,255,0.35)"}`,
                  boxShadow: activeSubfolder === "__inbox__" ? "0 0 0 3px rgba(34,226,255,0.18)" : "none",
                  minWidth: 140,
                }}
                data-testid="folder-pill-inbox"
                title={`${unfiledCount} item${unfiledCount === 1 ? "" : "s"} not yet in any folder`}
              >
                <span
                  className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
                  style={{ background: "rgba(34,226,255,0.22)" }}
                >
                  <Inbox size={14} color="#22E2FF" />
                </span>
                <div className="text-left">
                  <div className="text-[13px] font-semibold leading-tight text-white">Inbox</div>
                  <div className="text-[10px]" style={{ color: "rgba(255,255,255,0.65)" }}>
                    {unfiledCount} unfiled
                  </div>
                </div>
              </button>
            )}
            {visibleSubfolders.map((s) => {
              const count = subfolderCounts.get(s.id) || 0;
              const isActive = activeSubfolder === s.id;
              const empty = count === 0;
              const isFresh = freshFolderId === s.id;
              const isReorderTarget = dragOverFolderId === s.id && draggingFolderId && draggingFolderId !== s.id;
              const pillColor = s.color || null;
              return (
                <div
                  key={s.id}
                  className="relative shrink-0 meye-drop-target"
                  onDragOver={(e) => {
                    // Media drag (tile → folder) — handled here as before.
                    if (e.dataTransfer.types?.includes("application/x-meyecam-media-ids")) {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = "move";
                      e.currentTarget.classList.add("meye-drop-hover");
                      return;
                    }
                    // Folder reorder drag (host-only) — handled via component state.
                    if (isHost && draggingFolderId) {
                      e.preventDefault();
                      setDragOverFolderId(s.id);
                    }
                  }}
                  onDragLeave={(e) => {
                    e.currentTarget.classList.remove("meye-drop-hover");
                    if (dragOverFolderId === s.id) setDragOverFolderId(null);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove("meye-drop-hover");
                    // 1) Media tile drop → bulk move
                    try {
                      const raw = e.dataTransfer.getData("application/x-meyecam-media-ids");
                      const ids = raw ? JSON.parse(raw) : null;
                      if (Array.isArray(ids) && ids.length) {
                        handleDropOnFolder(s.id, ids);
                        return;
                      }
                    } catch (_) { /* fall through */ }
                    // 2) Folder reorder drop (host)
                    if (isHost && draggingFolderId && draggingFolderId !== s.id) {
                      handleReorderFolders(draggingFolderId, s.id);
                    }
                    setDraggingFolderId(null);
                    setDragOverFolderId(null);
                  }}
                  data-testid={`folder-drop-${s.id}`}
                  style={{
                    outline: isReorderTarget ? "2px dashed #22E2FF" : "none",
                    outlineOffset: 4,
                    borderRadius: 14,
                  }}
                >
                  <button
                    onClick={() => {
                      setActiveSubfolder(isActive ? null : s.id);
                      if (isFresh) { setFreshFolderId(null); setShowFreshTip(false); }
                    }}
                    onDoubleClick={(e) => { e.preventDefault(); downloadFolderZip(s); }}
                    onContextMenu={isHost ? (e) => {
                      e.preventDefault();
                      // Toggle a small color popover under the pill
                      setColorPopoverFor((cur) => (cur === s.id ? null : s.id));
                    } : undefined}
                    draggable={isHost}
                    onDragStart={isHost ? (e) => {
                      setDraggingFolderId(s.id);
                      e.dataTransfer.effectAllowed = "move";
                      // Use an empty payload to differentiate from media drags
                      try { e.dataTransfer.setData("application/x-meyecam-folder-id", s.id); } catch (_) { /* noop */ }
                    } : undefined}
                    onDragEnd={isHost ? () => { setDraggingFolderId(null); setDragOverFolderId(null); } : undefined}
                    className={`flex items-center gap-2 rounded-xl px-3 py-2 transition ${isFresh ? "meye-fresh-folder" : ""}`}
                    style={{
                      background: isActive
                        ? (pillColor
                            ? `linear-gradient(135deg, ${pillColor}55, rgba(34,226,255,0.20))`
                            : "linear-gradient(135deg, rgba(127,75,255,0.30), rgba(34,226,255,0.18))")
                        : (pillColor ? `${pillColor}22` : "rgba(255,255,255,0.04)"),
                      border: `1px solid ${isActive || isFresh ? (pillColor || "#7F4BFF") : (pillColor ? `${pillColor}77` : "rgba(255,255,255,0.10)")}`,
                      boxShadow: isActive ? `0 0 0 3px ${pillColor ? `${pillColor}33` : "rgba(127,75,255,0.18)"}` : "none",
                      opacity: empty && !isActive && !isFresh ? 0.62 : 1,
                      minWidth: 140,
                      cursor: isHost ? "grab" : "pointer",
                    }}
                    data-testid={`folder-pill-${s.id}`}
                    title={isHost
                      ? (empty ? "Empty — drag tiles in · right-click for color · drag to reorder · double-click to ZIP" : `${count} item${count === 1 ? "" : "s"} · right-click for color · drag to reorder`)
                      : (empty ? "Empty — drag tiles in to populate" : `${count} item${count === 1 ? "" : "s"} · double-click to ZIP`)}
                  >
                    <span
                      className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
                      style={{ background: isActive ? "rgba(255,255,255,0.10)" : "rgba(127,75,255,0.18)" }}
                    >
                      <Folder size={14} color={isActive ? "#fff" : "#C9B6FF"} />
                    </span>
                    <div className="text-left">
                      <div className="text-[13px] font-semibold leading-tight text-white truncate" style={{ maxWidth: 140 }}>
                        {s.name}
                      </div>
                      <div className="text-[10px]" style={{ color: empty ? "rgba(255,255,255,0.45)" : "rgba(255,255,255,0.65)" }}>
                        {empty ? "empty" : `${count} item${count === 1 ? "" : "s"}`}
                      </div>
                    </div>
                    {isFresh && (
                      <span
                        className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold ml-1"
                        style={{
                          background: "linear-gradient(90deg, #7F4BFF, #22E2FF)",
                          color: "#fff",
                          boxShadow: "0 0 16px rgba(34,226,255,0.55)",
                        }}
                        data-testid={`folder-fresh-badge-${s.id}`}
                      >
                        <Sparkles size={9} /> NEW
                      </span>
                    )}
                  </button>
                  {isFresh && showFreshTip && (
                    <div
                      className="absolute top-full mt-2 left-0 z-20 meye-fresh-tip"
                      style={{ pointerEvents: "none", whiteSpace: "nowrap" }}
                      data-testid={`folder-fresh-tip-${s.id}`}
                    >
                      <div
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium"
                        style={{
                          background: "linear-gradient(90deg, rgba(127,75,255,0.95), rgba(34,226,255,0.95))",
                          color: "#fff",
                          boxShadow: "0 12px 30px -8px rgba(127,75,255,0.55)",
                        }}
                      >
                        <Sparkles size={11} />
                        <span>Long-press tiles or tap <span className="font-bold">Select</span> to add items</span>
                      </div>
                    </div>
                  )}

                  {isHost && colorPopoverFor === s.id && (
                    <div
                      className="absolute top-full mt-2 left-0 z-30 p-2 rounded-xl flex items-center gap-1.5"
                      style={{
                        background: "rgba(15,12,28,0.96)",
                        border: "1px solid rgba(127,75,255,0.30)",
                        boxShadow: "0 20px 50px -10px rgba(0,0,0,0.55)",
                      }}
                      data-testid={`folder-color-popover-${s.id}`}
                    >
                      {FOLDER_COLORS.map((c) => (
                        <button
                          key={c.value || "none"}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRecolorFolder(s.id, c.value);
                            setColorPopoverFor(null);
                          }}
                          className="w-6 h-6 rounded-full transition hover:scale-110"
                          style={{
                            background: c.value || "transparent",
                            border: `2px solid ${pillColor === c.value ? "#fff" : (c.value ? c.value : "rgba(255,255,255,0.30)")}`,
                            position: "relative",
                          }}
                          title={c.name}
                          data-testid={`folder-color-${c.value || "none"}`}
                        >
                          {!c.value && (
                            <span
                              style={{
                                position: "absolute",
                                inset: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 12,
                                color: "rgba(255,255,255,0.65)",
                              }}
                            >
                              ×
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Active-folder breadcrumb — replaces the small "Show all" link with a
          big, obvious "← All photos" pill + the folder title + inline rename. */}
      {activeSubfolder && (
        <div
          className="glass-card flex items-center gap-3 mb-5 px-3 py-2.5"
          style={{
            background: "linear-gradient(135deg, rgba(127,75,255,0.10), rgba(34,226,255,0.06))",
            border: `1px solid ${(activeFolder?.color || "#7F4BFF")}77`,
          }}
          data-testid="folder-breadcrumb"
        >
          <button
            onClick={() => setActiveSubfolder(null)}
            className="meye-chip shrink-0"
            style={{
              padding: "8px 12px",
              fontSize: 13,
              fontWeight: 600,
              background: "rgba(255,255,255,0.06)",
              borderColor: "rgba(255,255,255,0.18)",
              color: "#fff",
            }}
            data-testid="clear-folder-filter"
          >
            <ArrowLeft size={14} /> All photos
          </button>
          <div className="flex-1 min-w-0 flex items-center gap-2">
            <span
              className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
              style={{ background: activeSubfolder === "__inbox__" ? "rgba(34,226,255,0.22)" : (activeFolder?.color ? `${activeFolder.color}33` : "rgba(127,75,255,0.22)") }}
            >
              {activeSubfolder === "__inbox__"
                ? <Inbox size={14} color="#22E2FF" />
                : <Folder size={14} color={activeFolder?.color || "#C9B6FF"} />}
            </span>
            {renamingFolderId === activeFolder?.id ? (
              <input
                autoFocus
                className="meye-input"
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value.slice(0, 40))}
                onKeyDown={(e) => {
                  if (e.key === "Enter") commitRenameFolder();
                  if (e.key === "Escape") cancelRenameFolder();
                }}
                onBlur={commitRenameFolder}
                maxLength={40}
                style={{ maxWidth: 260, padding: "6px 10px", fontSize: 14 }}
                data-testid="breadcrumb-rename-input"
              />
            ) : (
              <>
                <div className="min-w-0">
                  <div className="text-[15px] font-semibold leading-tight text-white truncate" data-testid="breadcrumb-folder-name">
                    {activeSubfolder === "__inbox__" ? "Inbox" : (activeFolder?.name || "Folder")}
                  </div>
                  <div className="text-[11px]" style={{ color: "rgba(255,255,255,0.55)" }}>
                    {filteredMedia.length} {filteredMedia.length === 1 ? "item" : "items"}
                    {activeSubfolder === "__inbox__" && " · not yet in any folder"}
                  </div>
                </div>
                {/* Only real folders are renameable — the Inbox pseudo-folder is read-only */}
                {activeFolder && canEditFolder(activeFolder) && (
                  <button
                    onClick={() => startRenameFolder(activeFolder)}
                    className="rounded-full p-1.5 transition hover:bg-white/10 shrink-0"
                    aria-label="Rename folder"
                    title="Rename folder"
                    data-testid="breadcrumb-rename-btn"
                  >
                    <Pencil size={13} color="rgba(255,255,255,0.7)" />
                  </button>
                )}
              </>
            )}
          </div>
          {/* Quick actions for the active folder */}
          {activeFolder && (
            <button
              onClick={() => downloadFolderZip(activeFolder)}
              className="meye-chip shrink-0"
              style={{
                padding: "6px 10px",
                fontSize: 11,
                background: "rgba(34,226,255,0.10)",
                borderColor: "rgba(34,226,255,0.45)",
                color: "#22E2FF",
              }}
              data-testid="breadcrumb-folder-zip"
              title="Download this folder as ZIP"
            >
              <Download size={11} /> ZIP
            </button>
          )}
        </div>
      )}

      {/* One-time "How to organize" tutorial — shows when the album has folders
          and the user hasn't dismissed it. Renders below the FOLDERS row so the
          arrows in the copy point at things on screen. */}
      {showOrganizeTip && visibleSubfolders.length > 0 && (
        <div
          className="glass-card p-4 mb-5 relative overflow-hidden"
          style={{
            background: "linear-gradient(135deg, rgba(127,75,255,0.12), rgba(34,226,255,0.08))",
            border: "1px solid rgba(127,75,255,0.35)",
          }}
          data-testid="organize-tip-banner"
        >
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -top-12 -right-12 w-40 h-40 rounded-full"
            style={{ background: "radial-gradient(closest-side, rgba(34,226,255,0.20), transparent 70%)" }}
          />
          <div className="flex items-start gap-3 relative">
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
              style={{ background: "rgba(127,75,255,0.22)" }}
            >
              <FolderPlus size={16} color="#C9B6FF" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-semibold text-white">Organize your photos</div>
              <ul className="text-[12px] mt-1.5 space-y-0.5" style={{ color: "rgba(255,255,255,0.75)" }}>
                <li>
                  • Tap <span className="font-semibold" style={{ color: "#22E2FF" }}>Select</span> then choose photos, or
                  drag any photo onto a folder above
                </li>
                <li>
                  • <span className="font-semibold">Long-press</span> a photo to enter Select mode instantly
                </li>
                <li className="hidden sm:list-item">
                  • <span className="font-semibold">Right-click</span> a folder to recolor ·
                  <span className="font-semibold"> double-click</span> to download as ZIP
                </li>
              </ul>
            </div>
            <button
              onClick={dismissOrganizeTip}
              className="meye-chip shrink-0"
              style={{
                padding: "6px 12px",
                fontSize: 12,
                background: "linear-gradient(135deg, rgba(127,75,255,0.30), rgba(34,226,255,0.20))",
                borderColor: "rgba(34,226,255,0.55)",
                color: "#fff",
              }}
              data-testid="organize-tip-dismiss"
            >
              <Check size={13} /> Got it
            </button>
            <button
              onClick={dismissOrganizeTip}
              className="rounded-full p-1.5 transition hover:bg-white/10 shrink-0"
              aria-label="Dismiss"
              data-testid="organize-tip-close"
            >
              <XIcon size={14} color="rgba(255,255,255,0.6)" />
            </button>
          </div>
        </div>
      )}

      {isHost && (
        <Link
          to={`/${slug}/messages`}
          className="meye-chip w-full justify-center mb-4"
          style={{ background: unread > 0 ? "rgba(127,75,255,0.14)" : "rgba(255,255,255,0.04)", borderColor: unread > 0 ? "#7F4BFF" : "rgba(255,255,255,0.10)" }}
          data-testid="open-inbox-btn"
          title="Messages guests have sent you about this album"
        >
          <Inbox size={14} />
          <span>{unread > 0 ? "Guest messages" : "Guest messages (empty)"}</span>
          {unread > 0 && (
            <span
              className="ml-1 inline-flex items-center justify-center text-[11px] font-bold"
              style={{ background: "#7F4BFF", color: "#fff", borderRadius: 999, padding: "1px 8px" }}
            >
              {unread} new
            </span>
          )}
        </Link>
      )}

      {/* Uploader filter chips */}
      {groups.length > 1 && (
        <div className="flex flex-wrap gap-2 mb-4" data-testid="uploader-tabs">
          <button
            className={`meye-chip ${activeUploader === null ? "active" : ""}`}
            style={activeUploader === null ? { "--chip-active-bg": "rgba(127,75,255,0.18)", "--chip-active-border": "#7F4BFF" } : {}}
            onClick={() => setActiveUploader(null)}
            data-testid="uploader-tab-all"
          >
            All ({media.length})
          </button>
          {myGroup && (
            <button
              className={`meye-chip ${activeUploader === myKey ? "active" : ""}`}
              style={activeUploader === myKey
                ? { "--chip-active-bg": "rgba(34,226,255,0.22)", "--chip-active-border": "#22E2FF" }
                : { background: "rgba(34,226,255,0.08)", borderColor: "rgba(34,226,255,0.45)", color: "#A8F2FF" }
              }
              onClick={() => setActiveUploader(myKey)}
              data-testid="uploader-tab-mine"
              title="Only show photos & videos you uploaded"
            >
              <Sparkles size={13} color="#22E2FF" />
              My uploads ({myGroup.items.length})
            </button>
          )}
          {groups.map((g) => (
            <button
              key={g.key}
              className={`meye-chip ${activeUploader === g.key ? "active" : ""}`}
              style={activeUploader === g.key ? { "--chip-active-bg": "rgba(127,75,255,0.18)", "--chip-active-border": "#7F4BFF" } : {}}
              onClick={() => setActiveUploader(g.key)}
              data-testid={`uploader-tab-${g.name}`}
            >
              From {g.name} ({g.items.length})
            </button>
          ))}
        </div>
      )}

      {media.length === 0 ? (
        <div className="glass-card p-8 text-center" style={{ color: "rgba(255,255,255,0.6)" }} data-testid="empty-gallery">
          No photos yet.<br />
          <Link to={`/${slug}/upload`} className="font-bold mt-2 inline-block" style={{ color: "#22E2FF" }}>
            Upload the first ones →
          </Link>
        </div>
      ) : activeSubfolder && filteredMedia.length === 0 ? (
        // Folder is selected but empty — nudge the user toward populating it.
        <div className="glass-card p-8 text-center" data-testid="empty-folder">
          <Folder size={28} color="#C9B6FF" style={{ display: "inline-block", marginBottom: 8 }} />
          <div style={{ color: "rgba(255,255,255,0.85)" }}>This folder is empty.</div>
          <div className="text-sm mt-1" style={{ color: "rgba(255,255,255,0.55)" }}>
            Tap <span className="font-semibold" style={{ color: "#22E2FF" }}>← Show all</span> above, then long-press any tile (or hit <span className="font-semibold">Select</span>) to move items in.
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-6" data-testid="uploader-groups">
          {visibleGroups.map((g) => (
            <UploaderFolder
              key={g.key}
              slug={slug}
              group={g}
              subfolders={subfolders}
              selectMode={selectMode}
              selected={selected}
              selectedIds={selectedIds}
              toggleSelected={toggleSelected}
              selectAllInItems={selectAllInItems}
              enterSelectMode={enterSelectMode}
              onLongPressTile={enterSelectWith}
              onOpenTile={openLightbox}
              myGuestId={getGuestId()}
              rankInfo={groupRanks.get(g.key)}
              totalGroups={groups.length}
            />
          ))}
        </div>
      )}

      <div className="mt-8 text-center">
        <Link to={`/${slug}/upload`} className="cta-pill" data-testid="add-more-btn">
          + Add more photos
        </Link>
      </div>

      {selectMode && selected.size > 0 && (
        <div
          className="fixed left-0 right-0 flex justify-center"
          style={{
            bottom: "max(16px, env(safe-area-inset-bottom))",
            zIndex: 50,
            padding: "0 12px",
          }}
          data-testid="select-action-bar"
        >
          <div
            className="glass-card flex items-center gap-2 px-3 py-2"
            style={{
              boxShadow: "0 24px 60px -16px rgba(127, 75, 255, 0.55), 0 18px 50px -20px rgba(34, 226, 255, 0.45)",
              borderColor: "rgba(127,75,255,0.55)",
              background: "rgba(20,15,35,0.92)",
              backdropFilter: "blur(20px)",
            }}
          >            <div className="text-sm font-semibold px-2" style={{ color: "#fff" }}>
              {selected.size} selected
            </div>
            <button
              onClick={handleSaveToDevice}
              disabled={busyAction}
              className="cta-pill"
              style={{ padding: "10px 14px", minHeight: 40, fontSize: 13 }}
              data-testid="save-to-device-btn"
            >
              <Smartphone size={14} /> Save
            </button>
            <button
              onClick={() => setMoveOpen(true)}
              disabled={busyAction}
              className="meye-chip"
              style={{ padding: "10px 14px", minHeight: 40, fontSize: 13, background: "rgba(127,75,255,0.16)", borderColor: "rgba(127,75,255,0.55)", color: "#C9B6FF" }}
              data-testid="move-selection-btn"
              title="Move to a folder (or create a new one)"
            >
              <FolderInput size={14} /> Folder
            </button>
            <button
              onClick={handleShareSelection}
              disabled={busyAction}
              className="meye-chip"
              style={{ padding: "10px 14px", minHeight: 40, fontSize: 13, background: "rgba(34,226,255,0.10)", borderColor: "rgba(34,226,255,0.45)", color: "#A8F2FF" }}
              data-testid="share-selection-btn"
              title="Forward the album link to someone"
            >
              <Share2 size={14} /> Share
            </button>
            <button
              onClick={handleZipSelection}
              disabled={busyAction}
              className="meye-chip"
              style={{ padding: "10px 14px", minHeight: 40, fontSize: 13, background: "rgba(34,226,255,0.10)", borderColor: "rgba(34,226,255,0.45)", color: "#22E2FF" }}
              data-testid="zip-selection-btn"
            >
              <FileArchive size={14} /> ZIP
            </button>
            <button
              onClick={handleDeleteSelection}
              disabled={busyAction}
              className="meye-chip"
              style={{ padding: "10px 14px", minHeight: 40, fontSize: 13, background: "rgba(255,107,107,0.10)", borderColor: "rgba(255,107,107,0.45)", color: "#FF6B6B" }}
              data-testid="delete-selection-btn"
              title="Delete selected (only items you uploaded)"
            >
              <Trash2 size={14} />
            </button>
          </div>
        </div>
      )}

      <MoveToFolderModal
        open={moveOpen}
        onClose={() => setMoveOpen(false)}
        onMove={handleMoveSelection}
        subfolders={subfolders}
        count={selected.size}
        isHost={isHost}
        myGuestId={getGuestId()}
      />

      {newFolderOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-3 sm:p-6"
          style={{ background: "rgba(0,0,0,0.72)", backdropFilter: "blur(6px)" }}
          onClick={() => !creatingFolder && setNewFolderOpen(false)}
          role="dialog"
          aria-modal="true"
          data-testid="new-folder-modal"
        >
          <div
            className="glass-card w-full max-w-sm p-5 sm:p-6 relative"
            onClick={(e) => e.stopPropagation()}
            style={{ boxShadow: "0 30px 80px -20px rgba(127,75,255,0.45)" }}
          >
            <button
              onClick={() => !creatingFolder && setNewFolderOpen(false)}
              className="absolute top-3 right-3 rounded-full p-2 transition hover:bg-white/10"
              aria-label="Close"
              data-testid="new-folder-modal-close"
            >
              <XIcon size={16} color="rgba(255,255,255,0.75)" />
            </button>
            <div
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-3"
              style={{ background: "rgba(127,75,255,0.18)", border: "1px solid rgba(127,75,255,0.45)" }}
            >
              <FolderPlus size={12} color="#C9B6FF" />
              <span className="text-[11px] font-semibold tracking-wide" style={{ color: "#C9B6FF" }}>
                NEW FOLDER
              </span>
            </div>
            <h3 className="text-xl font-semibold tracking-tight">Create a folder</h3>
            <p className="text-sm mt-1" style={{ color: "rgba(255,255,255,0.7)" }}>
              Then long-press tiles or hit <span className="font-semibold" style={{ color: "#fff" }}>Select</span> to move items into it.
            </p>
            <input
              autoFocus
              className="meye-input mt-4"
              placeholder="e.g. Ceremony, Speeches, Cake cutting"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value.slice(0, 40))}
              maxLength={40}
              data-testid="new-folder-input"
              onKeyDown={(e) => { if (e.key === "Enter") handleCreateFolder(); }}
            />
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => setNewFolderOpen(false)}
                disabled={creatingFolder}
                className="meye-chip flex-1 justify-center"
                data-testid="new-folder-cancel"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateFolder}
                disabled={creatingFolder || !newFolderName.trim()}
                className="cta-pill flex-1"
                style={{ opacity: !newFolderName.trim() ? 0.5 : 1 }}
                data-testid="new-folder-confirm"
              >
                {creatingFolder ? <div className="meye-spinner" /> : <><Check size={16}/> Create</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {lightboxId && (
        <Lightbox
          items={flatOrdered}
          startId={lightboxId}
          slug={slug}
          onClose={closeLightbox}
          isHost={isHost}
          coverMediaId={album?.cover_media_id || null}
          onSetCover={async (mid) => {
            try {
              const updated = await Albums.patch(slug, { cover_media_id: mid });
              setAlbum(updated);
              toast.success("Cover updated");
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Couldn't set cover");
            }
          }}
        />
      )}

      <SlideshowModal
        open={slideshowOpen}
        onClose={() => setSlideshowOpen(false)}
        album={album}
        media={flatOrdered}
      />

      <FindMeModal
        open={findMeOpen}
        onClose={() => setFindMeOpen(false)}
        slug={slug}
        media={flatOrdered}
        onMatches={(ids) => { setActiveFaceGuests([]); setFaceMatchIds(ids); }}
      />
    </PageShell>
  );
}

// Collapsible folder card for a single uploader. Renders subfolder chips inside.
const UploaderFolder = ({ slug, group, subfolders, selectMode, selected, selectedIds, toggleSelected, selectAllInItems, enterSelectMode, onLongPressTile, onOpenTile, myGuestId, rankInfo, totalGroups }) => {
  const [open, setOpen] = useState(true);
  const [activeSub, setActiveSub] = useState(null);
  const [zipBusy, setZipBusy] = useState(false);

  // Is this MY group? (the current viewer is the same guest who uploaded these)
  const isMine = !!(myGuestId && group.key === `gid:${myGuestId}`);

  // Per-uploader ZIP — POSTs the list of media ids in this group to /zip-selection
  // so the host (or the uploader) gets just this person's contributions.
  const handleZipUploader = async (e) => {
    e?.stopPropagation();
    if (zipBusy) return;
    setZipBusy(true);
    const tid = toast.loading(`Preparing ZIP for ${group.name}…`);
    try {
      const ids = group.items.map((m) => m.id);
      const API = process.env.REACT_APP_BACKEND_URL || "";
      const res = await fetch(`${API}/api/album/${slug}/zip-selection`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids }),
      });
      if (!res.ok) throw new Error("zip failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${slug}-from-${group.name.replace(/\W+/g, "-").toLowerCase()}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      toast.dismiss(tid);
      toast.success(`Downloaded ${ids.length} items`);
    } catch (err) {
      toast.dismiss(tid);
      toast.error("ZIP failed — try Download ZIP for the whole album");
    } finally { setZipBusy(false); }
  };
  // Which subfolders actually contain items from this uploader, with counts
  const subFromGroup = useMemo(() => {
    const counts = new Map();
    let none = 0;
    for (const m of group.items) {
      if (m.subfolder_id) counts.set(m.subfolder_id, (counts.get(m.subfolder_id) || 0) + 1);
      else none += 1;
    }
    const sublist = subfolders
      .filter((s) => counts.has(s.id))
      .map((s) => ({ id: s.id, name: s.name, count: counts.get(s.id) }));
    return { sublist, noneCount: none };
  }, [group.items, subfolders]);

  const visibleItems = useMemo(() => {
    if (activeSub === null) return group.items;
    if (activeSub === "__none__") return group.items.filter((m) => !m.subfolder_id);
    return group.items.filter((m) => m.subfolder_id === activeSub);
  }, [group.items, activeSub]);

  const hasSubchips = subFromGroup.sublist.length > 0;

  return (
    <div className="glass-card overflow-hidden" data-testid={`uploader-folder-${group.name}`}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3"
        style={{ background: "rgba(255,255,255,0.03)" }}
        data-testid={`uploader-folder-toggle-${group.name}`}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="flex items-center justify-center"
            style={{
              width: 36, height: 36, borderRadius: 10,
              background: "linear-gradient(135deg, rgba(127,75,255,0.25), rgba(34,226,255,0.25))",
              border: "1px solid rgba(255,255,255,0.10)",
              flexShrink: 0,
            }}
          >
            <User size={16} color="rgba(255,255,255,0.9)" />
          </div>
          <div className="text-left min-w-0">
            <div className="font-semibold truncate flex items-center gap-2">
              <span>From {group.name}</span>
              {isMine && (
                <span
                  className="text-[9px] font-bold px-1.5 py-0.5 rounded-full"
                  style={{
                    background: "linear-gradient(90deg, #7F4BFF, #22E2FF)",
                    color: "#fff",
                    boxShadow: "0 0 10px rgba(34,226,255,0.35)",
                  }}
                  title={rankInfo && totalGroups > 1
                    ? `You — ${rankInfo.total} item${rankInfo.total === 1 ? "" : "s"}, #${rankInfo.rank} of ${totalGroups} contributors`
                    : "That's you — these are your uploads"}
                  data-testid={`contribution-badge-${group.name}`}
                >
                  YOU · {group.items.length}
                  {rankInfo && totalGroups > 1 && (
                    <span className="ml-1 inline-flex items-center gap-0.5">
                      · #{rankInfo.rank}
                      {rankInfo.rank === 1 && <Crown size={9} style={{ marginLeft: 2 }} />}
                    </span>
                  )}
                </span>
              )}
            </div>
            <div className="text-xs" style={{ color: "rgba(255,255,255,0.5)" }}>
              {group.items.length} {group.items.length === 1 ? "item" : "items"}
              {hasSubchips && ` · ${subFromGroup.sublist.length} ${subFromGroup.sublist.length === 1 ? "folder" : "folders"}`}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Per-uploader "Select" — toggle. First click ticks all of this person's
              items; click again to clear just their items. Auto-enables select mode. */}
          {(() => {
            const gIds = group.items.map((m) => m.id);
            const selectedFromGroup = gIds.filter((id) => selected && selected.has(id)).length;
            const allSelected = selectedFromGroup === gIds.length && gIds.length > 0;
            // Three explicit states:
            //   1. Not in select mode        → "Select" (enables select mode, ticks nothing)
            //   2. select mode, 0 ticked     → "Select all"
            //   3. select mode, all ticked   → "Deselect all"
            //   3b. select mode, some ticked → "Select all" (fills in the rest)
            let label, Icon, isDeselect, action;
            if (!selectMode) {
              label = "Select";
              Icon = CheckSquare;
              isDeselect = false;
              action = () => {
                // Enter select mode WITHOUT ticking anything — user explicitly
                // confirms with "Select all" below or hand-picks tiles.
                enterSelectMode && enterSelectMode();
              };
            } else if (allSelected) {
              label = "Deselect all";
              Icon = XIcon;
              isDeselect = true;
              action = () => {
                gIds.forEach((id) => { if (selected.has(id)) toggleSelected(id); });
              };
            } else {
              label = "Select all";
              Icon = CheckSquare;
              isDeselect = false;
              action = () => selectAllInItems(group.items);
            }
            return (
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); action(); }}
                onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); action(); } }}
                className="meye-chip"
                style={{
                  padding: "9px 14px",
                  fontSize: 13,
                  fontWeight: 600,
                  minHeight: 38,
                  background: isDeselect
                    ? "rgba(255,107,107,0.14)"
                    : "linear-gradient(135deg, rgba(127,75,255,0.22), rgba(34,226,255,0.14))",
                  borderColor: isDeselect ? "rgba(255,107,107,0.55)" : "rgba(127,75,255,0.65)",
                  color: isDeselect ? "#FF6B6B" : "#fff",
                  cursor: "pointer",
                }}
                data-testid={`select-uploader-${group.name}`}
                title={isDeselect
                  ? `Deselect all of ${group.name}'s items`
                  : (selectMode
                    ? `Tick all ${gIds.length} item${gIds.length === 1 ? "" : "s"} from ${group.name}`
                    : `Enter select mode to pick from ${group.name}'s items`)}
              >
                <Icon size={14} />
                <span>{label}</span>
              </span>
            );
          })()}
          <span
            role="button"
            tabIndex={0}
            onClick={handleZipUploader}
            onKeyDown={(e) => { if (e.key === "Enter") handleZipUploader(e); }}
            className="meye-chip"
            style={{
              padding: "9px 14px",
              fontSize: 13,
              fontWeight: 600,
              minHeight: 38,
              background: "rgba(34,226,255,0.10)",
              borderColor: "rgba(34,226,255,0.45)",
              color: "#22E2FF",
              cursor: "pointer",
            }}
            data-testid={`zip-uploader-${group.name}`}
            title={`Download all ${group.items.length} item${group.items.length === 1 ? "" : "s"} from ${group.name} as ZIP`}
          >
            {zipBusy ? <div className="meye-spinner" style={{ width: 13, height: 13 }} /> : <Download size={13} />}
            <span>ZIP</span>
          </span>
          {open ? <ChevronUp size={18} color="rgba(255,255,255,0.6)" /> : <ChevronDown size={18} color="rgba(255,255,255,0.6)" />}
        </div>
      </button>

      {open && (
        <div className="p-3">
          {selectMode && (
            <div className="flex flex-wrap gap-1.5 mb-3">
              <button
                onClick={(e) => { e.stopPropagation(); selectAllInItems(visibleItems); }}
                className="meye-chip"
                style={{ padding: "6px 10px", fontSize: 12, minHeight: 32, background: "rgba(127,75,255,0.10)", borderColor: "rgba(127,75,255,0.45)" }}
                data-testid={`select-folder-${group.name}`}
              >
                <CheckSquare size={12} /> Select all in this folder ({visibleItems.length})
              </button>
            </div>
          )}

          {hasSubchips && (
            <div className="flex flex-wrap gap-1.5 mb-3" data-testid={`sub-chips-${group.name}`}>
              <button
                className={`meye-chip ${activeSub === null ? "active" : ""}`}
                style={{
                  padding: "6px 10px", fontSize: 12, minHeight: 32,
                  ...(activeSub === null ? { "--chip-active-bg": "rgba(34,226,255,0.18)", "--chip-active-border": "#22E2FF" } : {}),
                }}
                onClick={() => setActiveSub(null)}
                data-testid={`sub-chip-all-${group.name}`}
              >
                All ({group.items.length})
              </button>
              {subFromGroup.noneCount > 0 && (
                <button
                  className={`meye-chip ${activeSub === "__none__" ? "active" : ""}`}
                  style={{
                    padding: "6px 10px", fontSize: 12, minHeight: 32,
                    ...(activeSub === "__none__" ? { "--chip-active-bg": "rgba(34,226,255,0.18)", "--chip-active-border": "#22E2FF" } : {}),
                  }}
                  onClick={() => setActiveSub("__none__")}
                  data-testid={`sub-chip-none-${group.name}`}
                >
                  Unfiled ({subFromGroup.noneCount})
                </button>
              )}
              {subFromGroup.sublist.map((s) => (
                <button
                  key={s.id}
                  className={`meye-chip ${activeSub === s.id ? "active" : ""}`}
                  style={{
                    padding: "6px 10px", fontSize: 12, minHeight: 32,
                    ...(activeSub === s.id ? { "--chip-active-bg": "rgba(34,226,255,0.18)", "--chip-active-border": "#22E2FF" } : {}),
                  }}
                  onClick={() => setActiveSub(s.id)}
                  data-testid={`sub-chip-${s.id}`}
                >
                  <Folder size={11} /> {s.name} ({s.count})
                </button>
              ))}
            </div>
          )}

          <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-1.5" data-testid={`media-grid-${group.name}`}>
            {visibleItems.map((m) => (
              <MediaTile
                key={m.id}
                slug={slug}
                media={m}
                isSelected={selected && selected.has(m.id)}
                selectMode={selectMode}
                selectedIds={selectedIds}
                toggleSelected={toggleSelected}
                onLongPressTile={onLongPressTile}
                onOpenTile={onOpenTile}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Single tile — extracted so each instance can own its own long-press hook.
// Long-press on a tile (touch or mouse hold) drops the gallery into select-mode
// AND ticks the tile, so users don't have to find the "Select" button first.
// Desktop: tiles are draggable — drop on a folder pill to move them.
const MediaTile = ({ slug, media: m, isSelected, selectMode, toggleSelected, onLongPressTile, onOpenTile, selectedIds }) => {
  const { handlers, consumeFired } = useLongPress(() => onLongPressTile && onLongPressTile(m.id));

  const onDragStart = (e) => {
    // If this tile is part of an active selection, send ALL selected ids so a
    // user can multi-select then drag one of them to move the whole batch.
    // Otherwise it's a single-item drag.
    const ids = (selectMode && isSelected && selectedIds && selectedIds.length > 0)
      ? selectedIds
      : [m.id];
    e.dataTransfer.setData("application/x-meyecam-media-ids", JSON.stringify(ids));
    e.dataTransfer.effectAllowed = "move";
    document.body.classList.add("meye-dragging-media");
    // Show count in the drag image when batching
    if (ids.length > 1) {
      const ghost = document.createElement("div");
      ghost.textContent = `${ids.length} items`;
      ghost.style.cssText = "position:absolute;top:-1000px;left:-1000px;background:linear-gradient(135deg,#7F4BFF,#22E2FF);color:#fff;padding:6px 12px;border-radius:999px;font-size:12px;font-weight:600;font-family:inherit;";
      document.body.appendChild(ghost);
      e.dataTransfer.setDragImage(ghost, 30, 15);
      setTimeout(() => ghost.remove(), 0);
    }
  };
  const onDragEnd = () => {
    document.body.classList.remove("meye-dragging-media");
  };

  const tile = (
    <>
      {/* Try to show the thumb — for images it's always there; for videos
          it's there if a poster was uploaded during the upload flow. */}
      <img
        src={Media.thumbUrl(m.id)}
        alt={m.title || m.filename}
        loading="lazy"
        decoding="async"
        style={{ width: "100%", height: "100%", objectFit: "cover" }}
        onError={(e) => {
          // No thumb (legacy video, decode failure, network error) → fall back to gradient
          const img = e.currentTarget;
          img.style.display = "none";
          const fb = img.nextElementSibling;
          if (fb) fb.style.display = "flex";
        }}
      />
      {/* Fallback rendered behind the img — only revealed when img.onError fires. */}
      <div
        className="absolute inset-0 items-center justify-center"
        style={{ display: "none", background: "linear-gradient(135deg,#1a1230,#0a0a14)" }}
      >
        <Play size={28} color="#22E2FF" />
      </div>
      {/* Always overlay a small play badge on videos so the kind is obvious */}
      {m.is_video && (
        <div
          className="absolute bottom-1 left-1 rounded-md flex items-center gap-1 px-1.5 py-0.5 pointer-events-none"
          style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(6px)" }}
        >
          <Play size={10} color="#22E2FF" fill="#22E2FF" />
          <span className="text-[10px] font-medium" style={{ color: "#fff" }}>VIDEO</span>
        </div>
      )}
      {selectMode && (
        <div
          className="absolute inset-0 flex items-start justify-end p-1.5"
          style={{
            background: isSelected ? "rgba(127,75,255,0.30)" : "rgba(0,0,0,0.15)",
            boxShadow: isSelected ? "inset 0 0 0 3px #7F4BFF" : "none",
          }}
        >
          {isSelected ? (
            <CheckSquare size={20} color="#fff" style={{ background: "#7F4BFF", borderRadius: 4 }} />
          ) : (
            <Square size={20} color="rgba(255,255,255,0.9)" />
          )}
        </div>
      )}
    </>
  );

  if (selectMode) {
    return (
      <button
        onClick={() => toggleSelected(m.id)}
        className="relative rounded-lg overflow-hidden"
        style={{ aspectRatio: "1/1", background: "rgba(255,255,255,0.05)" }}
        data-testid={`media-tile-${m.id}`}
        aria-pressed={isSelected}
        draggable
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        {...handlers}
      >
        {tile}
      </button>
    );
  }

  // Not in select mode — long-press drops us in.
  // Click opens the in-place lightbox (instead of navigating to /media/:id).
  return (
    <a
      href={`/${slug}/media/${m.id}`}
      className="relative rounded-lg overflow-hidden block"
      style={{ aspectRatio: "1/1", background: "rgba(255,255,255,0.05)", WebkitTouchCallout: "none", cursor: "zoom-in" }}
      data-testid={`media-tile-${m.id}`}
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={(e) => {
        if (consumeFired()) {
          e.preventDefault();
          return;
        }
        // Cmd/Ctrl-click + middle-click etc → let the browser open in a new tab.
        if (e.metaKey || e.ctrlKey || e.button === 1) return;
        e.preventDefault();
        if (onOpenTile) onOpenTile(m.id);
      }}
      {...handlers}
    >
      {tile}
    </a>
  );
};
